// src/pages/ModeSelect.tsx
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

type Mode = "creator" | "brand";
function isMode(v: unknown): v is Mode {
  return v === "creator" || v === "brand";
}

export default function ModeSelect() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();

  const GLOBAL_KEY = "appcreativa_mode";

  // ✅ Estado visual + Ref como "fuente única" para evitar stale state
  const [mode, setMode] = useState<Mode>("creator");
  const selectedModeRef = useRef<Mode>("creator");

  const legacyUserKey = useMemo(() => {
    return user?.id ? `appcreativa_mode_${user.id}` : null;
  }, [user?.id]);

  const chooseMode = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return params.get("choose") === "1";
  }, [location.search]);

  const lastNavRef = useRef<string | null>(null);
  const safeReplace = useCallback(
    (to: string) => {
      const current = location.pathname + (location.search || "");
      if (current === to) return;
      if (lastNavRef.current === to) return;
      lastNavRef.current = to;
      navigate(to, { replace: true });
    },
    [navigate, location.pathname, location.search]
  );

  // ✅ Si cambia el pathname, resetea anti-loop (evita “segundo intento”)
  useEffect(() => {
    lastNavRef.current = null;
  }, [location.pathname]);

  const getSavedMode = useCallback((): Mode | null => {
    try {
      const raw = localStorage.getItem(GLOBAL_KEY);
      return isMode(raw) ? raw : null;
    } catch {
      return null;
    }
  }, []);

  // ✅ Migración legacy -> global (ok)
  useEffect(() => {
    if (!user) return;
    try {
      const globalRaw = localStorage.getItem(GLOBAL_KEY);
      const legacyRaw = legacyUserKey ? localStorage.getItem(legacyUserKey) : null;

      if (!isMode(globalRaw) && isMode(legacyRaw)) {
        localStorage.setItem(GLOBAL_KEY, legacyRaw);
      }

      if (legacyUserKey) localStorage.removeItem(legacyUserKey);
    } catch {}
  }, [user, legacyUserKey]);

  // ✅ Entra a selección o redirige según modo guardado
  useEffect(() => {
    if (loading) return;

    if (!user) {
      const next = encodeURIComponent("/mode?choose=1");
      safeReplace(`/login?next=${next}`);
      return;
    }

    const saved = getSavedMode();

    if (chooseMode) {
      // Estamos seleccionando: solo precargar si existe
      lastNavRef.current = null;
      if (saved) {
        setMode(saved);
        selectedModeRef.current = saved;
      }
      return;
    }

    // ✅ No estamos en “choose”: manda DIRECTO al generador del modo guardado
    if (saved === "creator") {
      safeReplace("/dashboard?tab=ia");
      return;
    }
    if (saved === "brand") {
      safeReplace("/brand/create");
      return;
    }

    // Si no hay saved, obligamos a elegir
    safeReplace("/mode?choose=1");
  }, [loading, user, chooseMode, getSavedMode, safeReplace]);

  const setSelectedMode = useCallback((m: Mode) => {
    selectedModeRef.current = m;
    setMode(m);
  }, []);

  const handleContinue = useCallback(() => {
    const chosen = selectedModeRef.current;

    try {
      localStorage.setItem(GLOBAL_KEY, chosen);
      if (legacyUserKey) localStorage.removeItem(legacyUserKey);
    } catch {}

    // ✅ Importantísimo: resetear anti-loop antes de navegar
    lastNavRef.current = null;

    // ✅ Ir directo al generador del modo elegido
    if (chosen === "creator") safeReplace("/dashboard?tab=ia");
    else safeReplace("/brand/create");
  }, [legacyUserKey, safeReplace]);

  return (
    <div className="min-h-screen bg-[#0b0b0f] text-white px-6 py-8 overflow-hidden relative">
      {/* Glow */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-indigo-500/10 blur-3xl" />
      <div className="pointer-events-none absolute top-32 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-purple-500/10 blur-3xl" />

      <div className="max-w-5xl mx-auto relative z-10">
        <div className="mb-4">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="text-sm text-gray-300 hover:text-white transition inline-flex items-center gap-2"
          >
            ← Volver al inicio
          </button>
        </div>

        <div className="mb-8 text-center">
          <div className="text-3xl font-bold tracking-tight">
            Ovylon <span className="text-sm text-gray-400 font-medium ml-2">AI Studio</span>
          </div>

          <p className="text-gray-400 text-sm mt-2 max-w-2xl mx-auto">
            Elige tu modo y configura tu experiencia según tu objetivo.
            Puedes regresar luego desde Configuración.
          </p>

          {chooseMode && (
            <p className="text-[12px] text-indigo-200 mt-2">
              Estás en modo selección. Elige y presiona <b>Continuar</b>.
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <button
            type="button"
            onClick={() => setSelectedMode("creator")}
            className={`text-left rounded-2xl border p-6 transition ${
              mode === "creator"
                ? "border-indigo-400/60 bg-indigo-500/10"
                : "border-white/10 bg-white/5 hover:bg-white/10"
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-gray-400">Creator Mode</div>
                <h2 className="mt-2 text-xl font-semibold">Creación libre</h2>
                <p className="mt-2 text-sm text-gray-400 leading-relaxed">
                  Para creadores, freelancers e ideas rápidas. Crea, prueba variaciones
                  y guarda resultados para producir con velocidad.
                </p>
              </div>

              <div
                className={`h-10 w-10 rounded-2xl flex items-center justify-center text-lg font-bold ${
                  mode === "creator"
                    ? "bg-gradient-to-br from-indigo-500 via-purple-500 to-cyan-400"
                    : "bg-white/10"
                }`}
              >
                ✨
              </div>
            </div>

            <ul className="mt-4 space-y-2 text-sm text-gray-300">
              <li>• Generación rápida para contenido diario</li>
              <li>• Historial de creaciones (prompts + resultados)</li>
              <li>• Variaciones para encontrar el mejor creativo</li>
              <li className="text-indigo-200">• Calidad 2K / 4K / 8K para uso comercial</li>
              <li>• Guardado de estilo visual</li>
            </ul>
          </button>

          <button
            type="button"
            onClick={() => setSelectedMode("brand")}
            className={`text-left rounded-2xl border p-6 transition ${
              mode === "brand"
                ? "border-indigo-400/60 bg-indigo-500/10"
                : "border-white/10 bg-white/5 hover:bg-white/10"
            }`}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-gray-400">Brand Mode</div>
                <h2 className="mt-2 text-xl font-semibold">Sistema para marcas</h2>
                <p className="mt-2 text-sm text-gray-400 leading-relaxed">
                  Para empresas, campañas e influencers pro.
                  Consistencia visual y producción repetible sin perder dirección.
                </p>
              </div>

              <div
                className={`h-10 w-10 rounded-2xl flex items-center justify-center text-lg font-bold ${
                  mode === "brand"
                    ? "bg-gradient-to-br from-indigo-500 via-purple-500 to-cyan-400"
                    : "bg-white/10"
                }`}
              >
                🏷️
              </div>
            </div>

            <ul className="mt-4 space-y-2 text-sm text-gray-300">
              <li>• Resultados listos para negocio</li>
              <li className="text-indigo-200">• Calidad 2K / 4K / 8K comercial</li>
              <li>• Consistencia visual persistente</li>
              <li>• Campañas y piezas repetibles</li>
              <li>• Producción continua (menos pruebas, más salida)</li>
            </ul>
          </button>
        </div>

        <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="text-sm text-gray-400">
            Seleccionado:{" "}
            <span className="text-gray-200 font-semibold">
              {mode === "creator" ? "Creator Mode" : "Brand Mode"}
            </span>
          </div>

          <button
            type="button"
            onClick={handleContinue}
            className="px-6 py-3 rounded-xl text-sm font-semibold
              bg-indigo-600 hover:bg-indigo-700
              shadow-lg shadow-indigo-500/30
              transition"
          >
            Continuar →
          </button>
        </div>
      </div>
    </div>
  );
}




